import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Upload, X, Eye, Check, Smartphone, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import QRCode from 'qrcode';

const PFLICHT_SLOTS = [
  { key: 'front_left', title: 'Fahrzeug vorne links', hint: 'Gesamtes Fahrzeug sichtbar.', minCount: 1 },
  { key: 'front_right', title: 'Fahrzeug vorne rechts', hint: 'Gesamtes Fahrzeug sichtbar.', minCount: 1 },
  { key: 'rear_left', title: 'Fahrzeug hinten links', hint: 'Gesamtes Fahrzeug sichtbar.', minCount: 1 },
  { key: 'rear_right', title: 'Fahrzeug hinten rechts', hint: 'Gesamtes Fahrzeug sichtbar.', minCount: 1 },
  { key: 'odometer', title: 'Kilometerstand', hint: 'Tacho gut lesbar fotografieren.', minCount: 1 },
  { key: 'damage_wide', title: 'Schadenbereich (weit)', hint: 'Schaden + Umgebung sichtbar.', minCount: 1 },
  { key: 'damage_detail', title: 'Schaden Detail (2 Fotos)', hint: '1x Winkel links, 1x Winkel rechts', minCount: 2 }
];

const OPTIONAL_SLOTS = [
  { key: 'registration', title: 'Fahrzeugschein', hint: 'Gut lesbar fotografieren.' },
  { key: 'vin', title: 'FIN/VIN', hint: 'Fahrzeugidentifikationsnummer.' },
  { key: 'interior', title: 'Innenraum', hint: 'Falls relevant für den Schaden.' }
];

const BEISPIEL_TIPPS = {
  front_left: ['Gute Beleuchtung, kein starker Schatten.', 'Gesamtes Fahrzeug im Bild.'],
  front_right: ['Gute Beleuchtung, kein starker Schatten.', 'Gesamtes Fahrzeug im Bild.'],
  rear_left: ['Komplette Rückansicht sichtbar.', 'Kennzeichen lesbar (wird später unkenntlich gemacht).'],
  rear_right: ['Komplette Rückansicht sichtbar.', 'Kennzeichen lesbar (wird später unkenntlich gemacht).'],
  odometer: ['Tacho komplett im Bild.', 'Scharf und gut lesbar.'],
  damage_wide: ['Schaden + Umgebung zeigen.', 'Nicht zu nah fotografieren.'],
  damage_detail: ['Mehrere Winkel aufnehmen.', 'Mindestens 2 Fotos.'],
  registration: ['Fahrzeugschein Teil I und/oder Teil II.', 'Alle Daten gut lesbar fotografieren.'],
  vin: [
    'FIN/VIN-Nummer (17-stellig) fotografieren.',
    'Typenschild befindet sich meist:',
    '• Im Türrahmen Fahrerseite (B-Säule)',
    '• Unter der Motorhaube am Rahmen',
    '• Im Kofferraum unter Verkleidung',
    '• Bei neueren Autos: unten Windschutzscheibe'
  ],
  interior: ['Innenraum nur falls relevant.', 'Bei Schäden im Innenraum.']
};

const BilderUploadPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [caseId, setCaseId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [uploads, setUploads] = useState({});
  const [progress, setProgress] = useState({ required_count: 0, required_total: 7, required_complete: false });
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [showExample, setShowExample] = useState(null);
  const [polling, setPolling] = useState(false);
  const [guidedMode, setGuidedMode] = useState(false);
  const [partnerCaseCount, setPartnerCaseCount] = useState(0);

  useEffect(() => {
    const cid = searchParams.get('case_id');
    const pid = searchParams.get('partner_id') || localStorage.getItem('partner_id');
    
    if (!cid) {
      navigate('/portal/neuer-fall');
      return;
    }
    
    setCaseId(cid);
    setPartnerId(pid);
    loadUploads(cid);
    loadPartnerCaseCount(pid);
    
    // Start polling for mobile uploads
    const interval = setInterval(() => loadUploads(cid), 4000);
    return () => clearInterval(interval);
  }, [searchParams, navigate]);

  const loadPartnerCaseCount = async (pid) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/${pid}`);
      const data = await response.json();
      const caseCount = data.total_cases || 0;
      setPartnerCaseCount(caseCount);
      
      // Enable Guided Mode for first 3 cases
      const guidedModeEnabled = localStorage.getItem(`guided_mode_${pid}`) !== 'disabled' && caseCount <= 3;
      setGuidedMode(guidedModeEnabled);
    } catch (error) {
      console.error('Load partner case count failed:', error);
    }
  };

  const loadUploads = async (cid) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/case/${cid}`);
      const data = await response.json();
      setUploads(data.slots || {});
      setProgress({
        required_count: data.required_count || 0,
        required_total: 7,
        required_complete: data.required_complete || false
      });
    } catch (error) {
      console.error('Load uploads failed:', error);
    }
  };

  const handleUpload = async (slotKey, file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('slot_key', slotKey);
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload fehlgeschlagen');

      const data = await response.json();
      
      // Update local state
      await loadUploads(caseId);
      
      // Show saved indicator briefly
      setTimeout(() => setLoading(false), 500);
    } catch (error) {
      alert(error.message);
      setLoading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!confirm('Bild wirklich löschen?')) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/uploads/image/${imageId}?case_id=${caseId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) throw new Error('Löschen fehlgeschlagen');
      
      await loadUploads(caseId);
    } catch (error) {
      alert(error.message);
    }
  };

  const generateQR = async () => {
    const mobileUrl = `${window.location.origin}/portal/mobile-upload/${caseId}`;
    const qr = await QRCode.toDataURL(mobileUrl, { width: 300, margin: 2 });
    setQrDataUrl(qr);
    setShowQR(true);
  };

  const canProceed = progress.required_complete;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Bilder hochladen</h1>
          <p className="text-gray-600 mb-6">Bitte lade die Pflichtbilder hoch. So können wir den Fall schneller bearbeiten.</p>
          
          {/* Guided Mode Banner */}
          {guidedMode && (
            <div className="mb-6 p-6 bg-gradient-to-r from-brand-green/10 to-brand-blue/10 border-2 border-brand-green/30 rounded-2xl max-w-4xl mx-auto">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-bold text-brand-green text-lg">🎯 Hilfe-Modus aktiv</h3>
                <button
                  onClick={() => {
                    setGuidedMode(false);
                    localStorage.setItem(`guided_mode_${partnerId}`, 'disabled');
                  }}
                  className="text-xs text-gray-600 hover:text-gray-900 underline"
                >
                  Ausblenden
                </button>
              </div>
              <p className="text-sm text-gray-800 mb-3">
                Dies sind deine ersten Fälle! Wir helfen dir mit zusätzlichen Erklärungen beim Foto-Upload. Nach 3 Fällen wird dieser Modus automatisch deaktiviert.
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <span className="px-2 py-1 bg-white rounded-lg font-semibold">Fall {partnerCaseCount}/3</span>
                <span>• Achte auf gute Beleuchtung</span>
                <span>• Halte die Kamera ruhig</span>
                <span>• Fotografiere im Querformat</span>
              </div>
            </div>
          )}

          {/* Foto-Guidelines */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl max-w-4xl mx-auto">
            <h3 className="font-semibold text-blue-900 mb-2">📸 Foto-Regeln</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-blue-800">
              <div>✓ Querformat</div>
              <div>✓ Hell & scharf</div>
              <div>✓ Kein Wackeln</div>
              <div>✓ Fahrzeug komplett</div>
              <div>✓ Kein Zoom</div>
              <div>✓ Schaden gesamt + Detail</div>
            </div>
          </div>
          
          {/* Noch fehlend Box */}
          {!progress.required_complete && (
            <div className="mb-6 p-4 bg-orange-50 border-2 border-orange-300 rounded-xl max-w-4xl mx-auto">
              <h3 className="font-bold text-orange-900 mb-2">⚠️ Noch fehlend</h3>
              <div className="text-sm text-orange-800">
                {PFLICHT_SLOTS.map(slot => {
                  const slotImages = Object.keys(uploads).filter(k => k.startsWith(slot.key));
                  const count = slotImages.length;
                  if (count < slot.minCount) {
                    return <div key={slot.key}>• {slot.title}: {count}/{slot.minCount}</div>;
                  }
                  return null;
                })}
              </div>
            </div>
          )}
          
          {/* Progress */}
          <div className="inline-flex items-center gap-4 bg-white border border-gray-200 rounded-2xl px-6 py-4">
            <span className="text-sm font-semibold text-gray-700">Pflichtbilder vollständig:</span>
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-bold ${progress.required_complete ? 'text-brand-green' : 'text-gray-900'}`}>
                {progress.required_count} / {progress.required_total}
              </span>
              {progress.required_complete && <Check className="w-6 h-6 text-brand-green" />}
            </div>
          </div>
          
          {/* Mobile Upload Button */}
          <div className="mt-4">
            <Button onClick={generateQR} variant="outline" className="border-2 border-gray-300">
              <Smartphone className="w-5 h-5 mr-2" />
              Mit Handy hochladen
            </Button>
          </div>
        </div>

        {/* Pflicht-Slots */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Pflichtbilder</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {PFLICHT_SLOTS.map(slot => (
              <SlotCard
                key={slot.key}
                slot={slot}
                images={uploads[slot.key] || []}
                onUpload={(file) => handleUpload(slot.key, file)}
                onDelete={handleDelete}
                onShowExample={() => setShowExample(slot.key)}
              />
            ))}
          </div>
        </div>

        {/* Optional Slots */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Optionale Bilder</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {OPTIONAL_SLOTS.map(slot => (
              <SlotCard
                key={slot.key}
                slot={slot}
                images={uploads[slot.key] || []}
                onUpload={(file) => handleUpload(slot.key, file)}
                onDelete={handleDelete}
                onShowExample={() => setShowExample(slot.key)}
                isOptional
              />
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button onClick={() => navigate(`/portal/neuer-fall?case_id=${caseId}&partner_id=${partnerId}`)} variant="outline" className="border-2 border-gray-300">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück
          </Button>
          <div className="relative group">
            <Button
              onClick={() => navigate(`/portal/video-upload?case_id=${caseId}&partner_id=${partnerId}`)}
              disabled={!canProceed}
              size="lg"
              className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Weiter zu Video
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            {!canProceed && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap">
                Bitte alle Pflichtbilder hochladen
              </div>
            )}
          </div>
        </div>

        {!canProceed && (
          <div className="mt-6 bg-orange-50 border border-orange-300 rounded-xl p-4">
            <p className="text-sm text-orange-800 font-semibold mb-2 text-center">
              ⚠️ Zum Fortfahren fehlen noch diese Pflichtbilder:
            </p>
            <ul className="text-sm text-orange-700 space-y-1 max-w-2xl mx-auto">
              {PFLICHT_SLOTS.filter(slot => {
                const slotImages = Object.keys(uploads).filter(k => k.startsWith(slot.key));
                const count = slotImages.length;
                return count < slot.minCount;
              }).map(s => (
                <li key={s.key} className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  <span>{s.title} ({Object.keys(uploads).filter(k => k.startsWith(s.key)).length}/{s.minCount})</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {showQR && (
        <QRModal qrDataUrl={qrDataUrl} caseId={caseId} onClose={() => setShowQR(false)} />
      )}

      {/* Example Modal */}
      {showExample && (
        <ExampleModal slotKey={showExample} onClose={() => setShowExample(null)} />
      )}
    </div>
  );
};

// Slot Card Component
const SlotCard = ({ slot, images, onUpload, onDelete, onShowExample, isOptional }) => {
  const fileInputRef = React.useRef();
  const hasImages = images.length > 0;
  const isComplete = images.length >= (slot.minCount || 1);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onUpload(file);
    }
  };

  return (
    <div className={`bg-white border-2 rounded-2xl p-6 transition-all ${
      isDragging ? 'border-brand-green bg-brand-green/5' : 
      isComplete ? 'border-brand-green/30' : 'border-gray-200'
    }`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{slot.title}</h3>
          <p className="text-sm text-gray-600">{slot.hint}</p>
          {slot.minCount > 1 && (
            <p className="text-xs text-brand-green mt-1">Mindestens {slot.minCount} Bilder erforderlich</p>
          )}
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
          isComplete ? 'bg-brand-green/10 text-brand-green' : 
          isOptional ? 'bg-gray-100 text-gray-600' : 'bg-yellow-50 text-yellow-700'
        }`}>
          {isComplete ? 'Erledigt' : isOptional ? 'Optional' : 'Fehlt'}
        </div>
      </div>

      {/* Thumbnails */}
      {hasImages && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {images.map((img, idx) => (
            <div key={img.image_id} className="relative group">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                alt={slot.title}
                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                onError={(e) => {
                  console.error('Image load failed:', img.url);
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f3f4f6" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%239ca3af" dy=".3em">Bild nicht verfügbar</text></svg>';
                }}
              />
              <button
                onClick={() => onDelete(img.image_id)}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      {(!hasImages || (slot.minCount && images.length < slot.minCount) || isOptional) && (
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-brand-green transition-all cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-2">
            {hasImages ? 'Weiteres Bild hinzufügen' : 'Datei wählen oder hierher ziehen'}
          </p>
          <p className="text-xs text-gray-500">JPG, PNG (max. 10MB)</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Actions */}
      <div className="mt-4">
        <button
          onClick={onShowExample}
          className="text-sm text-brand-green hover:text-brand-green-dark font-semibold flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          Beispiel ansehen
        </button>
      </div>
    </div>
  );
};

// QR Modal
const QRModal = ({ qrDataUrl, caseId, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={onClose}>
    <div className="bg-white rounded-3xl p-10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Mit Handy hochladen</h2>
      <p className="text-gray-600 mb-6 text-center">Scannen Sie den QR-Code mit Ihrem Smartphone</p>
      
      {qrDataUrl ? (
        <img src={qrDataUrl} alt="QR Code" className="w-full max-w-[300px] mx-auto mb-6" />
      ) : (
        <div className="w-full h-[300px] flex items-center justify-center">
          <Loader className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}
      
      <p className="text-xs text-gray-500 text-center mb-6">
        Oder öffnen Sie direkt:<br />
        <code className="text-brand-green">/portal/mobile-upload/{caseId}</code>
      </p>
      
      <Button onClick={onClose} variant="outline" className="w-full border-2 border-gray-300">
        Schließen
      </Button>
    </div>
  </div>
);

// Example Modal
const ExampleModal = ({ slotKey, onClose }) => {
  const tips = BEISPIEL_TIPPS[slotKey] || ['Gut beleuchtet fotografieren.', 'Scharf und vollständig.'];
  
  // Map slot keys to real uploaded example images (echte Fahrzeugfotos vom User)
  const exampleImages = {
    'front_left': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/xazw55qk_1FBC127B-6C7F-49CD-A335-07E606E6C3F2.png',
    'front_right': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/mlxkcaac_BCC0374D-B11F-4BD6-80D5-01DE2E914921.png',
    'rear_left': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/zr1uo24a_F0DC57D4-3DFC-416D-A5D4-9904BD1206DB.png',
    'rear_right': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/agn9j6w6_FB3C713A-1963-43EE-88F4-9E85B5154D69.png',
    'odometer': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/nx6omzuz_60096118-CC7F-4D25-B0F9-33CA10510CB0.png',
    'damage_wide': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/agzcv7b5_IMG_1401.jpeg',
    'damage_detail': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/z847riyh_IMG_1406.jpeg',
    'registration': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/sedkmmzf_D6DC12AC-4B38-4A69-B796-27D33DEDC3F3.png',
    'vin': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/flll5g1u_1A22F588-396F-4FEF-8679-14BF187B0EAB.png',
    'interior': 'https://customer-assets.emergentagent.com/job_case-flow-2/artifacts/i8gbum20_96FE98D8-C274-43B0-9458-9C52801D9997.png'
  };
  
  const imageUrl = exampleImages[slotKey] || `/examples/example_${slotKey}.png`;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-white rounded-3xl p-10 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Beispielbild</h2>
        
        {/* Example Image */}
        <div className="bg-gray-100 rounded-2xl mb-6 overflow-hidden">
          <img
            src={imageUrl}
            alt="Beispiel"
            className="w-full h-auto"
            onError={(e) => {
              e.target.src = '/examples/placeholder.png';
            }}
          />
        </div>
        
        {/* Tips */}
        <div className="space-y-3 mb-6">
          <p className="font-semibold text-gray-900">Tipps:</p>
          {tips.map((tip, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="w-6 h-6 bg-brand-green/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-brand-green" />
              </div>
              <p className="text-sm text-gray-700">{tip}</p>
            </div>
          ))}
        </div>
        
        <Button onClick={onClose} className="w-full bg-brand-green hover:bg-brand-green-dark text-white">
          Verstanden
        </Button>
      </div>
    </div>
  );
};

export default BilderUploadPage;
